import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import { AuthContext } from "../context/AuthProvider";
import { device } from "../lib/breakpoints";
import Button from "../components/Button";
import { trackEvent } from "../helpers/mixpanel";
import SEO from "../components/SEO";
import { APP_NAME } from "../helpers/constants";
import toast from "react-hot-toast";

const EmbedCode = () => {
  const { userInfo } = useContext(AuthContext);
  const [apiKey, setApiKey] = useState("");
  const [copied, setCopied] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState("professional");
  const [selectedPosition, setSelectedPosition] = useState("bottom-right");
  const [customization, setCustomization] = useState({
    primaryColor: "#3C8DBC",
    welcomeMessage: "Hi! How can we help you today?",
    showBranding: true,
    enableSound: true,
  });

  useEffect(() => {
    // Generate API key based on user ID
    if (userInfo?.id) {
      setApiKey(
        `bm_${userInfo.id.substring(0, 8)}_${Math.random()
          .toString(36)
          .substring(2, 10)}`
      );
    }
  }, [userInfo?.id]);

  const generateEmbedCode = () => {
    const config = {
      apiKey: apiKey,
      theme: selectedTheme,
      position: selectedPosition,
      primaryColor: customization.primaryColor,
      welcomeMessage: customization.welcomeMessage,
      showBranding: customization.showBranding,
      enableSound: customization.enableSound,
      clinicName: userInfo?.clinicName || "Your Clinic",
    };

    return `<!-- LandlordOps Widget -->
<script>
  window.LandlordOpsConfig = ${JSON.stringify(config, null, 2)};
</script>
<script src="https://api.landlordops.com/widget.js" async></script>
<!-- End LandlordOps Widget -->`;
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(generateEmbedCode());
      setCopied(true);
      toast.success("Embed code copied to clipboard!");
      trackEvent("Embed Code Copied", {
        clinic_name: userInfo?.clinicName,
        theme: selectedTheme,
        position: selectedPosition,
      });
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      toast.error("Failed to copy to clipboard");
    }
  };

  const themes = [
    {
      id: "professional",
      name: "Professional",
      description: "Clean and modern design for property sites",
      preview: "🏢",
    },
    {
      id: "friendly",
      name: "Friendly",
      description: "Warm and approachable for resident-facing pages",
      preview: "😊",
    },
    {
      id: "minimal",
      name: "Minimal",
      description: "Simple and elegant design",
      preview: "⚪",
    },
    {
      id: "luxury",
      name: "Luxury",
      description: "Premium design for high-end practices",
      preview: "✨",
    },
  ];

  const positions = [
    { id: "bottom-right", label: "Bottom Right", icon: "↘️" },
    { id: "bottom-left", label: "Bottom Left", icon: "↙️" },
    { id: "bottom-center", label: "Bottom Center", icon: "⬇️" },
    { id: "side-right", label: "Side Right", icon: "➡️" },
  ];

  const platforms = [
    {
      name: "WordPress",
      instructions: [
        "Copy the embed code above",
        "Go to your WordPress admin dashboard",
        "Navigate to Appearance > Theme Editor",
        "Find your theme's footer.php file",
        "Paste the code before the closing </body> tag",
        "Save the file",
      ],
      icon: "🔗",
    },
    {
      name: "Squarespace",
      instructions: [
        "Copy the embed code above",
        "Go to your Squarespace admin panel",
        "Navigate to Settings > Advanced > Code Injection",
        "Paste the code in the Footer section",
        "Save your changes",
      ],
      icon: "⬛",
    },
    {
      name: "Wix",
      instructions: [
        "Copy the embed code above",
        "Go to your Wix Editor",
        "Click on Add > More > HTML Code",
        "Paste the embed code",
        "Position the element and publish your site",
      ],
      icon: "🌐",
    },
    {
      name: "Shopify",
      instructions: [
        "Copy the embed code above",
        "Go to your Shopify admin",
        "Navigate to Online Store > Themes",
        "Click Actions > Edit Code",
        "Find theme.liquid and paste code before </body>",
        "Save the file",
      ],
      icon: "🛒",
    },
    {
      name: "Custom HTML",
      instructions: [
        "Copy the embed code above",
        "Paste it anywhere in your HTML document",
        "Ideally place it before the closing </body> tag",
        "The widget will automatically initialize",
      ],
      icon: "💻",
    },
  ];

  return (
    <Container>
      <SEO
        title={`Embed Widget Code - ${APP_NAME} | Add Chat to Your Website`}
        description="Get your LandlordOps widget embed code and add Leo AI-powered tenant chat to your listing pages in minutes."
        keywords="embed code, website widget, property management chat widget, tenant AI assistant, installation guide"
      />

      {/* Header */}
      <Header>
        <PageTitle>Widget Embed Code</PageTitle>
        <PageSubtitle>
          Add the {APP_NAME} widget to your website in just a few minutes
        </PageSubtitle>
      </Header>

      {/* Main Content */}
      <ContentGrid>
        {/* Left Column - Configuration */}
        <ConfigurationSection>
          <Section>
            <SectionTitle>Customize Your Widget</SectionTitle>

            {/* Theme Selection */}
            <ConfigGroup>
              <ConfigLabel>Choose Theme</ConfigLabel>
              <ThemeGrid>
                {themes.map((theme) => (
                  <ThemeCard
                    key={theme.id}
                    $selected={selectedTheme === theme.id}
                    onClick={() => setSelectedTheme(theme.id)}
                  >
                    <ThemePreview>{theme.preview}</ThemePreview>
                    <ThemeName>{theme.name}</ThemeName>
                    <ThemeDescription>{theme.description}</ThemeDescription>
                  </ThemeCard>
                ))}
              </ThemeGrid>
            </ConfigGroup>

            {/* Position Selection */}
            <ConfigGroup>
              <ConfigLabel>Widget Position</ConfigLabel>
              <PositionGrid>
                {positions.map((position) => (
                  <PositionCard
                    key={position.id}
                    $selected={selectedPosition === position.id}
                    onClick={() => setSelectedPosition(position.id)}
                  >
                    <PositionIcon>{position.icon}</PositionIcon>
                    <PositionLabel>{position.label}</PositionLabel>
                  </PositionCard>
                ))}
              </PositionGrid>
            </ConfigGroup>

            {/* Customization Options */}
            <ConfigGroup>
              <ConfigLabel>Customization</ConfigLabel>
              <CustomizationOptions>
                <OptionRow>
                  <OptionLabel>Primary Color</OptionLabel>
                  <ColorInput
                    type="color"
                    value={customization.primaryColor}
                    onChange={(e) =>
                      setCustomization((prev) => ({
                        ...prev,
                        primaryColor: e.target.value,
                      }))
                    }
                  />
                </OptionRow>

                <OptionRow>
                  <OptionLabel>Welcome Message</OptionLabel>
                  <TextInput
                    type="text"
                    value={customization.welcomeMessage}
                    onChange={(e) =>
                      setCustomization((prev) => ({
                        ...prev,
                        welcomeMessage: e.target.value,
                      }))
                    }
                    placeholder="Enter welcome message..."
                  />
                </OptionRow>

                <OptionRow>
                  <CheckboxOption>
                    <input
                      type="checkbox"
                      checked={customization.showBranding}
                      onChange={(e) =>
                        setCustomization((prev) => ({
                          ...prev,
                          showBranding: e.target.checked,
                        }))
                      }
                    />
                    <span>Show "Powered by {APP_NAME}" branding</span>
                  </CheckboxOption>
                </OptionRow>

                <OptionRow>
                  <CheckboxOption>
                    <input
                      type="checkbox"
                      checked={customization.enableSound}
                      onChange={(e) =>
                        setCustomization((prev) => ({
                          ...prev,
                          enableSound: e.target.checked,
                        }))
                      }
                    />
                    <span>Enable notification sounds</span>
                  </CheckboxOption>
                </OptionRow>
              </CustomizationOptions>
            </ConfigGroup>
          </Section>

          {/* Widget Preview */}
          <Section>
            <SectionTitle>Preview</SectionTitle>
            <PreviewContainer>
              <PreviewFrame>
                <PreviewWidget
                  $position={selectedPosition}
                  $color={customization.primaryColor}
                  $theme={selectedTheme}
                >
                  <WidgetIcon>💬</WidgetIcon>
                  <WidgetBubble>{customization.welcomeMessage}</WidgetBubble>
                </PreviewWidget>
              </PreviewFrame>
              <PreviewNote>
                This is a simplified preview. The actual widget will be fully
                interactive.
              </PreviewNote>
            </PreviewContainer>
          </Section>
        </ConfigurationSection>

        {/* Right Column - Code & Instructions */}
        <CodeSection>
          <Section>
            <SectionTitle>Embed Code</SectionTitle>
            <CodeContainer>
              <CodeHeader>
                <CodeTitle>Copy this code to your website:</CodeTitle>
                <CopyButton onClick={handleCopyCode} $copied={copied}>
                  {copied ? "✅ Copied!" : "📋 Copy Code"}
                </CopyButton>
              </CodeHeader>
              <CodeBlock>
                <pre>{generateEmbedCode()}</pre>
              </CodeBlock>
            </CodeContainer>
          </Section>

          <Section>
            <SectionTitle>Installation Instructions</SectionTitle>
            <PlatformTabs>
              {platforms.map((platform, index) => (
                <PlatformCard key={index}>
                  <PlatformHeader>
                    <PlatformIcon>{platform.icon}</PlatformIcon>
                    <PlatformName>{platform.name}</PlatformName>
                  </PlatformHeader>
                  <InstructionsList>
                    {platform.instructions.map((instruction, stepIndex) => (
                      <InstructionItem key={stepIndex}>
                        <StepNumber>{stepIndex + 1}</StepNumber>
                        <StepText>{instruction}</StepText>
                      </InstructionItem>
                    ))}
                  </InstructionsList>
                </PlatformCard>
              ))}
            </PlatformTabs>
          </Section>

          {/* Additional Info */}
          <Section>
            <SectionTitle>Important Notes</SectionTitle>
            <InfoCard>
              <InfoList>
                <InfoItem>
                  <InfoIcon>🔧</InfoIcon>
                  <InfoText>
                    The widget will automatically appear on all pages of your
                    website
                  </InfoText>
                </InfoItem>
                <InfoItem>
                  <InfoIcon>📱</InfoIcon>
                  <InfoText>
                    Fully responsive and works on desktop, tablet, and mobile
                    devices
                  </InfoText>
                </InfoItem>
                <InfoItem>
                  <InfoIcon>⚡</InfoIcon>
                  <InfoText>
                    Lightweight code that won't slow down your website
                  </InfoText>
                </InfoItem>
                <InfoItem>
                  <InfoIcon>🔒</InfoIcon>
                  <InfoText>
                    Secure, modern tenant and prospect communication
                  </InfoText>
                </InfoItem>
                <InfoItem>
                  <InfoIcon>🎨</InfoIcon>
                  <InfoText>
                    You can update styling and settings anytime from this
                    dashboard
                  </InfoText>
                </InfoItem>
              </InfoList>
            </InfoCard>
          </Section>

          {/* Support */}
          <Section>
            <SectionTitle>Need Help?</SectionTitle>
            <SupportCard>
              <SupportText>
                Having trouble installing the widget? Our team is here to help!
              </SupportText>
              <SupportButtons>
                <SupportButton href="mailto:support@landlordops.com">
                  📧 Email Support
                </SupportButton>
                <SupportButton href="/contact">💬 Live Chat</SupportButton>
              </SupportButtons>
            </SupportCard>
          </Section>
        </CodeSection>
      </ContentGrid>
    </Container>
  );
};

export default EmbedCode;

// Styled Components
const Container = styled.div`
  width: 100%;
  background: var(--color-bg-primary);
  padding: 2rem;
  min-height: 100vh;

  @media ${device.mobile} {
    padding: 1rem;
  }
`;

const Header = styled.header`
  margin-bottom: 2rem;
  text-align: center;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 0.5rem;
  font-family: "Inter", sans-serif;

  @media ${device.mobile} {
    font-size: 1.75rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.1rem;
  color: var(--color-text-secondary);
  font-family: "Inter", sans-serif;
  margin: 0;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  max-width: 1400px;
  margin: 0 auto;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ConfigurationSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const CodeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Section = styled.section`
  background: var(--color-bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-medium);
  padding: 2rem;

  @media ${device.mobile} {
    padding: 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.3rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 1.5rem;
  font-family: "Inter", sans-serif;
`;

const ConfigGroup = styled.div`
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ConfigLabel = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 1rem;
  font-family: "Inter", sans-serif;
`;

const ThemeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
`;

const ThemeCard = styled.div`
  padding: 1rem;
  border: 2px solid
    ${(props) =>
      props.$selected ? "var(--color-primary)" : "var(--color-border-light)"};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  background: ${(props) =>
    props.$selected ? "var(--color-accent-light)" : "var(--color-bg-primary)"};

  &:hover {
    border-color: var(--color-primary);
    box-shadow: var(--shadow-light);
  }
`;

const ThemePreview = styled.div`
  font-size: 2rem;
  margin-bottom: 0.5rem;
`;

const ThemeName = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 0.25rem;
  font-family: "Inter", sans-serif;
`;

const ThemeDescription = styled.p`
  font-size: 0.8rem;
  color: var(--color-text-secondary);
  font-family: "Inter", sans-serif;
  margin: 0;
  line-height: 1.3;
`;

const PositionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
`;

const PositionCard = styled.div`
  padding: 1rem;
  border: 2px solid
    ${(props) =>
      props.$selected ? "var(--color-primary)" : "var(--color-border-light)"};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  background: ${(props) =>
    props.$selected ? "var(--color-accent-light)" : "var(--color-bg-primary)"};

  &:hover {
    border-color: var(--color-primary);
    box-shadow: var(--shadow-light);
  }
`;

const PositionIcon = styled.div`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
`;

const PositionLabel = styled.div`
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--color-text-primary);
  font-family: "Inter", sans-serif;
`;

const CustomizationOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const OptionLabel = styled.label`
  font-size: 0.9rem;
  color: var(--color-text-primary);
  font-family: "Inter", sans-serif;
  font-weight: 500;
  flex: 1;
`;

const ColorInput = styled.input`
  width: 50px;
  height: 40px;
  border: 1px solid var(--color-border-medium);
  border-radius: 8px;
  cursor: pointer;
  background: none;
`;

const TextInput = styled.input`
  flex: 1;
  padding: 0.5rem;
  border: 1px solid var(--color-border-medium);
  border-radius: 6px;
  font-family: "Inter", sans-serif;
  max-width: 200px;
`;

const CheckboxOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: var(--color-text-primary);
  font-family: "Inter", sans-serif;
  cursor: pointer;
`;

const PreviewContainer = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 2rem;
  position: relative;
`;

const PreviewFrame = styled.div`
  background: white;
  border-radius: 8px;
  height: 300px;
  position: relative;
  border: 1px solid var(--color-border-light);
  overflow: hidden;
`;

const PreviewWidget = styled.div`
  position: absolute;
  ${(props) => {
    switch (props.$position) {
      case "bottom-left":
        return "bottom: 20px; left: 20px;";
      case "bottom-center":
        return "bottom: 20px; left: 50%; transform: translateX(-50%);";
      case "side-right":
        return "right: 20px; top: 50%; transform: translateY(-50%);";
      default:
        return "bottom: 20px; right: 20px;";
    }
  }}

  display: flex;
  align-items: center;
  gap: 1rem;
`;

const WidgetIcon = styled.div`
  width: 60px;
  height: 60px;
  background: ${(props) => props.color || "var(--color-primary)"};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  cursor: pointer;
  box-shadow: var(--shadow-medium);
`;

const WidgetBubble = styled.div`
  background: white;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  box-shadow: var(--shadow-medium);
  font-size: 0.8rem;
  max-width: 150px;
  border: 1px solid var(--color-border-light);
  font-family: "Inter", sans-serif;
`;

const PreviewNote = styled.p`
  font-size: 0.8rem;
  color: var(--color-text-muted);
  font-family: "Inter", sans-serif;
  margin-top: 1rem;
  text-align: center;
  font-style: italic;
`;

const CodeContainer = styled.div`
  background: var(--color-bg-primary);
  border-radius: 12px;
  border: 1px solid var(--color-border-light);
  overflow: hidden;
`;

const CodeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid var(--color-border-light);
`;

const CodeTitle = styled.h4`
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--color-text-primary);
  font-family: "Inter", sans-serif;
  margin: 0;
`;

const CopyButton = styled(Button)`
  background: ${(props) =>
    props.$copied ? "#10b981" : "var(--color-primary)"};
  color: white;
  padding: 0.5rem 1rem;
  font-size: 0.8rem;
  border-radius: 6px;
  border: none;
  transition: all 0.3s ease;
`;

const CodeBlock = styled.div`
  padding: 1.5rem;
  background: #1e1e1e;
  color: #f8f8f2;
  font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
  font-size: 0.8rem;
  line-height: 1.4;
  overflow-x: auto;

  pre {
    margin: 0;
    white-space: pre-wrap;
    word-break: break-all;
  }
`;

const PlatformTabs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const PlatformCard = styled.div`
  background: var(--color-bg-primary);
  border-radius: 12px;
  border: 1px solid var(--color-border-light);
  padding: 1.5rem;
`;

const PlatformHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const PlatformIcon = styled.span`
  font-size: 1.5rem;
`;

const PlatformName = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  font-family: "Inter", sans-serif;
  margin: 0;
`;

const InstructionsList = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const InstructionItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const StepNumber = styled.span`
  background: var(--color-primary);
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 600;
  font-family: "Inter", sans-serif;
  flex-shrink: 0;
`;

const StepText = styled.span`
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  font-family: "Inter", sans-serif;
  line-height: 1.5;
`;

const InfoCard = styled.div`
  background: var(--color-bg-primary);
  border-radius: 12px;
  border: 1px solid var(--color-border-light);
  padding: 1.5rem;
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

const InfoIcon = styled.span`
  font-size: 1.2rem;
  flex-shrink: 0;
`;

const InfoText = styled.span`
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  font-family: "Inter", sans-serif;
  line-height: 1.5;
`;

const SupportCard = styled.div`
  background: var(--color-bg-primary);
  border-radius: 12px;
  border: 1px solid var(--color-border-light);
  padding: 1.5rem;
  text-align: center;
`;

const SupportText = styled.p`
  font-size: 1rem;
  color: var(--color-text-secondary);
  font-family: "Inter", sans-serif;
  margin-bottom: 1.5rem;
`;

const SupportButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;

  @media ${device.mobile} {
    flex-direction: column;
  }
`;

const SupportButton = styled.a`
  background: var(--gradient-primary);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-family: "Inter", sans-serif;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: var(--shadow-medium);
  }
`;
